import { useApolloClient } from '@apollo/client/react';
import { App } from 'antd';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';

import { endSession } from '@/api/session';
import { IDLE_ACTIVITY_STORAGE_KEY, IDLE_TIMEOUT_MS } from '@/shared/constants';
import useAppNavigate from '@/shared/hooks/useAppNavigation';
import useIdleTimeout from '@/shared/hooks/useIdleTimeout';
import { ROUTES } from '@/shared/routes';
import { useAppSelector } from '@/store';

/**
 * Пускает только авторизованного сотрудника. Иначе уводит на вход и кладёт
 * в state `savedUrl` — адрес, куда вернуться после входа. Так работает и
 * прямой заход гостя по ссылке, и истечение сессии (см. `api/session.ts`).
 *
 * После 15 минут бездействия во всех вкладках сессия завершается: кабинет
 * показывает персональные данные клиентов, оставленный без присмотра экран
 * не должен оставаться открытым.
 */
const ProtectedRoute: React.FC = () => {
  const isAuthorized = useAppSelector((state) => state.auth.isAuthorized);
  const { navigate } = useAppNavigate();
  const location = useLocation();
  const client = useApolloClient();
  const { notification } = App.useApp();
  const { t } = useTranslation();

  useIdleTimeout({
    enabled: isAuthorized,
    timeoutMs: IDLE_TIMEOUT_MS,
    storageKey: IDLE_ACTIVITY_STORAGE_KEY,
    onIdle: () => {
      notification.info({
        title: t('auth.idleLogout.title'),
        description: t('auth.idleLogout.description'),
        duration: 0
      });
      void endSession(client);
    }
  });

  useEffect(() => {
    if (isAuthorized) return;

    navigate(ROUTES.login, {
      state: { savedUrl: `${location.pathname}${location.search}` }
    });
  }, [isAuthorized]);

  if (!isAuthorized) return null;

  return <Outlet />;
};

export default ProtectedRoute;
